import React from 'react';

import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import { AxiosError } from 'axios';
import { useForm } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';

import { useCreateEntity } from 'src/features/entity/api/useCreateEntity';
import IdentifierForm from 'src/features/membership/components/forms/identifier/IdentifierForm';
import WaiveObligationsModal from 'src/features/membership/components/forms/WaiveObligationsModal';
import { IDENTIFIER_TYPE, MembershipIdentifier } from 'src/features/membership/types';
import {
    membershipIdentifierFormSchema,
    MembershipIdentifierFormSchemaValues,
} from 'src/features/membershipType/formSchema';
import { useListActiveStays } from 'src/features/stay/api/useListActiveStays';
import { useParking } from 'src/providers/ParkingProvider';
import SquareIcon from 'src/registry/components/blocks/SquareIcon';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { SmartParkError } from 'src/registry/types/axios';
import { Button } from 'src/registry/ui/Button';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
    DialogTrigger,
} from 'src/registry/ui/Dialog';
import { generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';
import { useOnError } from 'src/utils/errors';

interface IdentifierFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedIdentifier?: MembershipIdentifier;
    children?: React.ReactNode;
    isOpen?: boolean;
    isPending: boolean;
    icon: IconProp;
    title: string;
    subtitle: string;
    mutate: (
        data: MembershipIdentifierFormSchemaValues,
        onSuccess: () => void,
        onError: (error: AxiosError<SmartParkError, any>) => void,
        isValidated?: boolean,
    ) => void;
    isClient?: boolean;
}

function IdentifierFormModal<TData>({
    selectedIdentifier,
    className,
    isPending,
    children,
    icon,
    title,
    subtitle,
    mutate,
    isClient = false,
}: IdentifierFormModalProps<TData>) {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const [isConfirmOpen, setIsConfirmOpen] = React.useState(false);
    const [pendingValues, setPendingValues] = React.useState<MembershipIdentifierFormSchemaValues | null>(null);
    const [mainIdentifier, setMainIdentifier] = React.useState('');

    const { parkingId } = useParking();
    const { data: activeStaysList } = useListActiveStays({
        parkingId,
        search: mainIdentifier,
        config: {
            enabled: !!mainIdentifier,
        },
    });

    const onError = useOnError();

    const defaultValues = React.useMemo(
        () =>
            selectedIdentifier
                ? {
                      ...selectedIdentifier,
                      owner: selectedIdentifier.owner?.id,
                  }
                : {
                      ...(isClient ? { identifier: { value: undefined, type: IDENTIFIER_TYPE.LICENSE_PLATE } } : {}),
                  },
        [selectedIdentifier, isClient],
    );

    const form = useForm<MembershipIdentifierFormSchemaValues>({
        resolver: zodResolver(membershipIdentifierFormSchema),
        defaultValues,
    });

    const { mutate: createEntity } = useCreateEntity({});
    // defaultValues are cached and require the reset API to update
    React.useEffect(() => {
        form.reset(defaultValues);
    }, [isModalOpen, defaultValues, form]);

    const onOpenChange = (isOpen: boolean) => {
        if (!isOpen) form.reset();
        setIsModalOpen(isOpen);
    };

    const submit = React.useCallback(
        (values: MembershipIdentifierFormSchemaValues, isValidated?: boolean) => {
            if (values.entity) {
                createEntity(
                    { parkingId, ...values.entity },
                    {
                        onSuccess: (entity) => {
                            const newValues = {
                                ...values,
                                owner: entity.id,
                            };
                            mutate(
                                newValues,
                                () => setIsModalOpen(false),
                                (error) => onError(error, form.setError),
                                isValidated,
                            );
                        },
                    },
                );
            } else
                mutate(
                    values,
                    () => setIsModalOpen(false),
                    (error) => {
                        onError(error, form.setError);
                        setMainIdentifier('');
                    },
                    isValidated,
                );
        },
        [mutate, createEntity, onError, form.setError, parkingId],
    );

    // On create we don't submit immediately: first ask whether to waive the stay's obligations and
    // forward the answer as is_validated. Otherwise (edit) submit directly.
    React.useEffect(() => {
        if (!activeStaysList) return;

        const values = form.getValues();
        const activeStay = activeStaysList.results.find((result) => {
            if (result.main_identifier.value === mainIdentifier) {
                setPendingValues(values);
                setIsConfirmOpen(true);
                return result;
            }
        });

        if (!activeStay) submit(values);
    }, [activeStaysList]);

    const onSubmit = React.useCallback(
        (values: MembershipIdentifierFormSchemaValues) => setMainIdentifier(values.identifier.value),
        [],
    );

    const handleConfirm = React.useCallback(
        (isValidated: boolean) => {
            setIsConfirmOpen(false);
            if (pendingValues) submit(pendingValues, isValidated);
        },
        [pendingValues, submit],
    );

    return (
        <>
            <Dialog modal open={isModalOpen} onOpenChange={onOpenChange}>
                {children ? (
                    <DialogTrigger asChild>{children}</DialogTrigger>
                ) : (
                    <DialogTrigger asChild>
                        <Button className={className}>
                            <FontAwesomeIcon icon={ICON_MAP.ADD} />
                            {title}
                        </Button>
                    </DialogTrigger>
                )}
                <DialogContent>
                    <DialogHeader className="flex-row gap-2">
                        <SquareIcon icon={icon} />
                        <div>
                            <DialogTitle>{title}</DialogTitle>
                            <DialogDescription>{subtitle}</DialogDescription>
                        </div>
                    </DialogHeader>
                    <DialogSeparator />
                    <DialogBody>
                        <IdentifierForm form={form} onSubmit={onSubmit} isClient={isClient} />
                    </DialogBody>
                    <DialogSeparator />
                    <DialogFooter>
                        <DialogClose asChild>
                            <Button variant="outline" type="button" className="flex-1">
                                <FormattedMessage {...generalMessages.cancel} />
                            </Button>
                        </DialogClose>
                        <Button
                            variant="default"
                            disabled={isPending || !form.formState.isDirty}
                            type="submit"
                            onClick={form.handleSubmit(onSubmit)}
                            className="flex-1"
                        >
                            {isPending && (
                                <FontAwesomeIcon icon={ICON_MAP.SPINNER} className="mr-2 size-4 animate-spin" />
                            )}
                            {title}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
            <WaiveObligationsModal
                handleConfirm={handleConfirm}
                isConfirmOpen={isConfirmOpen}
                setIsConfirmOpen={setIsConfirmOpen}
            />
        </>
    );
}

export default IdentifierFormModal;
